import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Trash2, Check } from "lucide-react";
import {
  fetchSecurityLogins, fetchSuspiciousEvents, ackSuspicious,
  fetchActiveSessions, revokeSession, update2FASettings,
} from "@admin/services/adminApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, DataTable, FilterBar, Pill, StatCard } from "@admin/components/Primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function fmtDt(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s.slice(0, 19); }
}

export default function AdminSecurity() {
  const { user, checkAuth } = useAuth();
  const [logins, setLogins] = useState({ total: 0, items: [] });
  const [suspicious, setSuspicious] = useState({ total: 0, items: [] });
  const [sessions, setSessions] = useState({ total: 0, items: [], current_session_token: "" });
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [l, s, sess] = await Promise.all([
        fetchSecurityLogins({ suspicious: filter === "suspicious" ? "true" : undefined, limit: 30 }),
        fetchSuspiciousEvents({ limit: 30 }),
        fetchActiveSessions(),
      ]);
      setLogins(l);
      setSuspicious(s);
      setSessions(sess);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load security data"); }
    setBusy(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onAck = async (eid) => {
    try { await ackSuspicious(eid); toast.success("Acknowledged"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onRevoke = async (tok) => {
    if (!window.confirm("Revoke this session? The user will be signed out.")) return;
    try { await revokeSession(tok); toast.success("Session revoked"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onToggle2FA = async () => {
    if (user?.two_factor_required) {
      toast.error("2FA is mandatory for your role and cannot be disabled.");
      return;
    }
    try {
      const next = !user?.two_factor_enabled;
      await update2FASettings(next);
      toast.success(next ? "2FA enabled" : "2FA disabled");
      await checkAuth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const loginColumns = [
    { key: "created_at", label: "When", render: (r) => <span className="text-xs text-slate-600">{fmtDt(r.created_at)}</span> },
    {
      key: "email",
      label: "Admin",
      render: (r) => (
        <div className="min-w-[180px]">
          <p className="font-bold text-[#0A2540] text-sm">{r.email || "—"}</p>
          <code className="text-[10px] text-slate-400">{(r.user_id || "").slice(0, 12)}</code>
        </div>
      ),
    },
    { key: "ip", label: "IP", render: (r) => <code className="text-xs">{r.ip || "—"}</code> },
    { key: "ua_family", label: "Device", render: (r) => <span className="text-xs">{r.ua_family || "unknown"}</span> },
    {
      key: "suspicious",
      label: "Flag",
      render: (r) => r.suspicious
        ? <Pill color="rose"><ShieldAlert size={10} /> {r.suspicious_reason || "suspicious"}</Pill>
        : <Pill color="emerald">OK</Pill>,
    },
  ];

  const suspiciousColumns = [
    { key: "created_at", label: "When", render: (r) => <span className="text-xs text-slate-600">{fmtDt(r.created_at)}</span> },
    { key: "email", label: "Admin", render: (r) => <span className="font-bold text-sm">{r.email}</span> },
    { key: "ip", label: "IP", render: (r) => <code className="text-xs">{r.ip}</code> },
    { key: "reason", label: "Reason", render: (r) => <Pill color="rose">{r.reason}</Pill> },
    {
      key: "acknowledged",
      label: "State",
      render: (r) => r.acknowledged
        ? <Pill color="emerald"><Check size={10} /> Acknowledged</Pill>
        : <Pill color="amber">Unread</Pill>,
    },
    {
      key: "actions",
      label: "",
      render: (r) => !r.acknowledged && (
        <button onClick={() => onAck(r.id)} className="px-2 py-1 text-[11px] font-bold rounded-md border border-slate-200 hover:border-[#0A2540]" data-testid={`ack-${r.id}`}>
          Mark reviewed
        </button>
      ),
    },
  ];

  const sessionColumns = [
    { key: "created_at", label: "Started", render: (r) => <span className="text-xs">{fmtDt(r.created_at)}</span> },
    { key: "expires_at", label: "Expires", render: (r) => <span className="text-xs">{fmtDt(r.expires_at)}</span> },
    {
      key: "two_factor_verified_at",
      label: "2FA",
      render: (r) => r.two_factor_verified_at
        ? <Pill color="emerald">Verified</Pill>
        : <Pill color="amber">Not verified</Pill>,
    },
    {
      key: "session_token",
      label: "Token",
      render: (r) => <code className="text-[10px] text-slate-400">{(r.session_token || "").slice(0, 18)}…</code>,
    },
    {
      key: "is_current",
      label: "",
      render: (r) => r.is_current
        ? <Pill color="gold">This session</Pill>
        : (
          <button onClick={() => onRevoke(r.session_token)} className="px-2 py-1 text-[11px] font-bold rounded-md text-rose-700 border border-rose-200 hover:bg-rose-50" data-testid={`revoke-session-${r.session_token.slice(0,10)}`}>
            <Trash2 size={10} className="inline mr-1" /> Revoke
          </button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        testid="admin-security-header"
        title="Security"
        subtitle="Recent sign-ins, suspicious activity, active sessions, and your 2FA status."
      />

      {/* 2FA panel */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="security-2fa-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Two-factor authentication</p>
            <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              {user?.two_factor_enabled
                ? <><ShieldCheck size={16} className="inline text-emerald-600" /> 2FA is active on your account</>
                : <><ShieldAlert size={16} className="inline text-amber-600" /> 2FA is currently disabled</>}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {user?.two_factor_required
                ? "Mandatory for your role — cannot be disabled."
                : "Optional, but strongly recommended for any admin account."}
            </p>
          </div>
          <button
            onClick={onToggle2FA}
            disabled={user?.two_factor_required}
            data-testid="toggle-2fa"
            className={`px-4 py-2 rounded-xl font-bold text-sm border transition ${
              user?.two_factor_enabled
                ? "border-rose-200 text-rose-700 hover:bg-rose-50"
                : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {user?.two_factor_enabled ? "Disable 2FA" : "Enable 2FA"}
          </button>
        </div>
      </section>

      {/* Counters */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <StatCard testid="sec-stat-logins" label="Recent sign-ins" value={logins.total} accent="navy" />
        <StatCard testid="sec-stat-suspicious" label="Suspicious events" value={suspicious.total} hint={suspicious.items.filter((s) => !s.acknowledged).length + " unread"} accent={suspicious.items.some((s) => !s.acknowledged) ? "rose" : "emerald"} />
        <StatCard testid="sec-stat-sessions" label="Active sessions" value={sessions.total} accent="gold" />
      </div>

      {/* Suspicious events */}
      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Suspicious sign-ins</h2>
      <DataTable testid="suspicious-table" columns={suspiciousColumns} rows={suspicious.items} empty="No suspicious events" />

      {/* Logins table */}
      <div className="mt-8">
        <div className="flex justify-between items-end mb-3">
          <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400">Recent sign-ins</h2>
          <FilterBar testid="logins-filter">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sign-ins</SelectItem>
                <SelectItem value="suspicious">Suspicious only</SelectItem>
              </SelectContent>
            </Select>
          </FilterBar>
        </div>
        <DataTable testid="logins-table" columns={loginColumns} rows={logins.items} empty={busy ? "Loading…" : "No sign-in activity yet"} />
      </div>

      {/* Sessions table */}
      <div className="mt-8">
        <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Active sessions</h2>
        <DataTable testid="sessions-table" columns={sessionColumns} rows={sessions.items} empty="No active sessions" />
        <p className="mt-2 text-[11px] text-slate-400">
          You can't revoke the session you're currently using — sign out from the sidebar instead.
        </p>
      </div>
    </>
  );
}
